import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import { RootState } from './store';
import { Box, CircularProgress } from '@mui/material';
import { loginSuccess } from './store/slices/authSlice';
import { fetchUserSuccess } from './store/slices/userSlice';
import { fetchCompanyStart, fetchCompanySuccess, fetchCompanyFailure } from './store/slices/companySlice';
import { authService } from './services/authService';
import { fetchAndHydrateCurrentUser } from './services/fetchAndHydrateCurrentUser';
import { GET_COMPANY } from './graphql/queries';

// レイアウトコンポーネント
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';
import AdminLayout from './components/layouts/AdminLayout';
import RoleplayLayout from './components/layouts/RoleplayLayout';

// ページコンポーネント
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ScenarioListPage from './pages/scenarios/ScenarioListPage';
import RoleplayPage from './pages/roleplay/RoleplayPage';
import RoleplaySettingsPage from './pages/roleplay/RoleplaySettingsPage';
import EvaluationPage from './pages/evaluation/EvaluationPage';
import ChangePasswordPage from './pages/profile/ChangePasswordPage';
import FaqPage from './pages/help/FaqPage';
import InquiryPage from './pages/help/InquiryPage';
import NotFoundPage from './pages/NotFoundPage';

// 管理者ページコンポーネント
import UserManagementPage from './pages/admin/UserManagementPage';
import ScenarioManagementPage from './pages/admin/ScenarioManagementPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import ProfileManagementPage from './pages/admin/ProfileManagementPage';

import { canAccess, PermissionKey } from './utils/permissions';

const adminFallbacks: { key: PermissionKey; path: string }[] = [
  { key: 'userMgmt.view.canView', path: '/admin/users' },
  { key: 'scenarioMgmt.view.canView', path: '/admin/scenarios' },
  { key: 'analytics.view.canView', path: '/admin/analytics' },
  { key: 'profileMgmt.view.canView', path: '/admin/profiles' },
];

function getFirstAllowedAdminPath(currentUser: RootState['user']['currentUser']): string | null {
  const firstAllowed = adminFallbacks.find((f) => canAccess(currentUser, f.key));
  return firstAllowed ? firstAllowed.path : null;
}

// 認証が必要なルートのラッパーコンポーネント
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = React.useState(true);

  // コンポーネントがマウントされたときに認証状態を確認
  React.useEffect(() => {
    const checkAuth = async () => {
      try {
        // トークンの存在を確認
        const token = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
        const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
        
        if (!token) {
          // console.log('ProtectedRoute: トークンが見つかりません');
          setIsCheckingAuth(false);
          return;
        }
        
        // トークンの有効期限をチェック
        if (authService.isTokenExpired()) {
          // console.log('ProtectedRoute: トークンの有効期限が切れています。更新を試みます。');
          
          // リフレッシュトークンがない場合は更新できない
          if (!refreshToken) {
            console.error('ProtectedRoute: リフレッシュトークンが見つかりません');
            setIsCheckingAuth(false);
            return;
          }
          
          // トークンを更新
          const result = await authService.refreshToken();
          if (!result.success) {
            console.error('ProtectedRoute: トークンの更新に失敗しました:', result.message);
            setIsCheckingAuth(false);
            return;
          }
          
          // console.log('ProtectedRoute: トークンの更新に成功しました');
          
          // 認証状態を更新
          if (result.token) {
            dispatch(loginSuccess(result.token));
          }
        }
        
        // 認証状態を確認
        const isAuth = authService.isAuthenticated();
        if (!isAuth) {
          // console.log('ProtectedRoute: 認証されていません');
        }
      } catch (error) {
        console.error('ProtectedRoute: 認証状態の確認中にエラーが発生しました:', error);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, [dispatch]);

  if (loading || isCheckingAuth) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // 認証されていない場合はログインページにリダイレクト（元のパスを保持してディープリンク復帰を可能にする）
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  return <>{children}</>;
};

// 管理者専用ルートのラッパーコンポーネント
const AdminRoute: React.FC<{ children: React.ReactNode; requires?: PermissionKey }> = ({ children, requires }) => {
  const { isAuthenticated, loading } = useSelector((state: RootState) => state.auth);
  const { currentUser } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = React.useState(true);

  // コンポーネントがマウントされたときに認証状態を確認
  React.useEffect(() => {
    const checkAuth = async () => {
      try {
        // トークンの存在を確認
        const token = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
        const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
        
        if (!token) {
          // console.log('AdminRoute: トークンが見つかりません');
          setIsCheckingAuth(false);
          return;
        }
        
        // トークンの有効期限をチェック
        if (authService.isTokenExpired()) {
          // console.log('AdminRoute: トークンの有効期限が切れています。更新を試みます。');
          
          // リフレッシュトークンがない場合は更新できない
          if (!refreshToken) {
            console.error('AdminRoute: リフレッシュトークンが見つかりません');
            setIsCheckingAuth(false);
            return;
          }
          
          // トークンを更新
          const result = await authService.refreshToken();
          if (!result.success) {
            console.error('AdminRoute: トークンの更新に失敗しました:', result.message);
            setIsCheckingAuth(false);
            return;
          }
          
          // console.log('AdminRoute: トークンの更新に成功しました');
          
          // 認証状態を更新
          if (result.token) {
            dispatch(loginSuccess(result.token));
          }
        }
        
        // 認証状態を確認
        const isAuth = authService.isAuthenticated();
        if (!isAuth) {
          // console.log('AdminRoute: 認証されていません');
        }
      } catch (error) {
        console.error('AdminRoute: 認証状態の確認中にエラーが発生しました:', error);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, [dispatch]);

  if (loading || isCheckingAuth) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // 認証されていない場合はログインページにリダイレクト（元のパスを保持してディープリンク復帰を可能にする）
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  // 管理者でない場合はホームページにリダイレクト
  if (!['ADMIN', 'SYSTEM_ADMIN'].includes(currentUser?.userRole || '')) {
    return <Navigate to="/" replace />;
  }

  // セクション毎の権限チェック（指定があるとき）
  if (requires && !canAccess(currentUser, requires)) {
    // 許可された最初のタブへフォールバック。全不可ならトップへ戻す
    const firstAllowedPath = getFirstAllowedAdminPath(currentUser);
    return <Navigate to={firstAllowedPath || '/'} replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const dispatch = useDispatch();
  const client = useApolloClient();

  // ログイン直後の User 情報（departments, profile を含む）を取得する関数
  const fetchFullUserInfo = async (userId: string) => {
    await fetchAndHydrateCurrentUser(client, dispatch, userId);
  };

  // Company情報を取得する関数
  const fetchCompanyInfo = async (companyId: string) => {
    try {
      dispatch(fetchCompanyStart());
      const companyResult = await client.query({
        query: GET_COMPANY,
        variables: { companyId }
      });
      
      if (companyResult.data?.getCompany) {
        dispatch(fetchCompanySuccess(companyResult.data.getCompany));
      }
    } catch (error) {
      console.error('Company情報の取得に失敗しました:', error);
      dispatch(fetchCompanyFailure('Company情報の取得に失敗しました'));
    }
  };

  // アプリケーションの起動時に認証状態を確認
  useEffect(() => {
    const checkAndRestoreAuth = async () => {
      try {
        // トークンの存在を確認
        const token = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
        const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
        
        if (!token) {
          // console.log('トークンが見つかりません');
          return;
        }
        
        // トークンの有効期限をチェック
        if (authService.isTokenExpired()) {
          // console.log('トークンの有効期限が切れています。更新を試みます。');
          
          // リフレッシュトークンがない場合は更新できない
          if (!refreshToken) {
            console.error('リフレッシュトークンが見つかりません');
            return;
          }
          
          // トークンを更新
          const result = await authService.refreshToken();
          if (!result.success) {
            console.error('トークンの更新に失敗しました:', result.message);
            return;
          }
          
          // console.log('トークンの更新に成功しました');
        }

        // 認証状態を確認
        const isAuthenticated = authService.isAuthenticated();
        if (isAuthenticated) {
          // console.log('認証状態を復元します');
          
          // トークンが有効な場合、認証状態を復元
          const currentToken = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
          if (currentToken) {
            dispatch(loginSuccess(currentToken));
            
            // ユーザー情報を取得して復元
            const userInfo = authService.getCurrentUser();
            if (userInfo) {
              // ユーザー情報を補完
              const completeUser = {
                ...userInfo,
                displayName: userInfo.displayName || userInfo.email || 'ユーザー',
                disable: false,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              };
              
              // デバッグ用：ユーザー情報をコンソールに出力
              // console.log('App init - User info:', completeUser);
              dispatch(fetchUserSuccess(completeUser));
              
              // Company情報を取得
              if (userInfo.companyId) {
                await fetchCompanyInfo(userInfo.companyId);
              }

              // departments / profile など JWT に載っていない情報を取得して上書き
              if (userInfo.userId) {
                await fetchFullUserInfo(userInfo.userId);
              }
            } else {
              console.error('ユーザー情報が見つかりません');
            }
          } else {
            console.error('トークンが見つかりません（認証状態の復元中）');
          }
        } else {
          // console.log('認証されていません');
        }
      } catch (error) {
        console.error('認証状態の確認中にエラーが発生しました:', error);
      }
    };

    checkAndRestoreAuth();
  }, [dispatch]);

  const { currentUser } = useSelector((state: RootState) => state.user);
  const firstAllowedAdminPath = getFirstAllowedAdminPath(currentUser);

  return (
    <Routes>
      {/* 認証ルート */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/:companyId" element={<LoginPage />} />
        <Route path="/login/sso" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* 
          実際の実装では、以下のルートも追加
          <Route path="/register" element={<RegisterPage />} />
        */}
      </Route>

      {/* 保護されたルート - RoleplayLayout */}
      <Route
        element={
          <ProtectedRoute>
            <RoleplayLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/roleplay/:id" element={<RoleplayPage />} />
      </Route>

      {/* 保護されたルート - MainLayout */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/scenarios" replace />} />
        <Route path="/scenarios" element={<ScenarioListPage />} />
        <Route path="/roleplay-settings/:id" element={<RoleplaySettingsPage />} />
        <Route path="/evaluation/:id" element={<EvaluationPage />} />
        <Route path="/profile/change-password" element={<ChangePasswordPage />} />
        <Route path="/help" element={<Navigate to="/help/faq" replace />} />
        <Route path="/help/faq" element={<FaqPage />} />
        <Route path="/help/inquiry" element={<InquiryPage />} />
      </Route>

      {/* 管理者ルート */}
      <Route
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route path="/admin" element={<Navigate to={firstAllowedAdminPath || '/'} replace />} />
        <Route
          path="/admin/users"
          element={
            <AdminRoute requires="userMgmt.view.canView">
              <UserManagementPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/scenarios"
          element={
            <AdminRoute requires="scenarioMgmt.view.canView">
              <ScenarioManagementPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <AdminRoute requires="analytics.view.canView">
              <AnalyticsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/profiles"
          element={
            <AdminRoute requires="profileMgmt.view.canView">
              <ProfileManagementPage />
            </AdminRoute>
          }
        />
      </Route>

      {/* 404ページ */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default App;
